/**
 * Product Search API Service
 *
 * This service handles API calls to retrieve product information with:
 * - Request deduplication and caching
 * - Cookie-based country/language pricing
 * - Retry logic with exponential backoff
 *
 * Usage:
 * import productSearchApi from './services/productSearchApi';
 *
 * const products = await productSearchApi.getSkuDetails(['SKU1', 'SKU2']);
 * const pricing = await productSearchApi.fetchPricingData(['SKU1', 'SKU2']);
 */

import baseUrl from "../api/config.js";

class ProductSearchApiService {
  constructor() {
    // Configuration
    this.maxRetries = 3;
    this.retryDelay = 1000; // milliseconds
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes in milliseconds

    // API configuration with environment-based baseUrl
    this.apiConfig = {
      baseUrl: baseUrl,
      timeout: 15000, // 15 seconds
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };

    // State management
    this.cache = new Map();
    this.pendingRequests = new Map(); // Track ongoing requests to prevent duplicates
    this.requestTimeouts = new Map(); // Track request timeouts
  }

  /**
   * Get SKU details for multiple catalog numbers
   * @param {Array|string} skus - Array of SKU strings or single SKU
   * @returns {Promise<Array>} Array of product objects
   */
  async getSkuDetails(skus) {
    try {
      // Normalize input to array
      const skuArray = Array.isArray(skus) ? skus : [skus];

      if (skuArray.length === 0) {
        return [];
      }

      // Clear expired cache entries before processing
      this.clearExpiredCache();

      // Check cache first for all SKUs
      const cachedResults = [];
      const uncachedSkus = [];

      skuArray.forEach((sku) => {
        const cached = this.cache.get(sku);
        if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
          cachedResults.push(...cached.data);
        } else {
          uncachedSkus.push(sku);
        }
      });

      // If all results are cached, return them
      if (uncachedSkus.length === 0) {
        return cachedResults;
      }

      // Make single API call for all uncached SKUs
      const allResults = [...cachedResults];

      if (uncachedSkus.length > 0) {
        const apiResults = await this.fetchSkuChunk(uncachedSkus);
        allResults.push(...apiResults);
      }

      return allResults;
    } catch (error) {
      console.error("[ProductSearchAPI] Error in getSkuDetails:", error);
      return [];
    }
  }

  /**
   * List products for catalog numbers (alias for getSkuDetails for compatibility)
   * @param {Array|string} catalogNumbers - Array of catalog numbers or single catalog number
   * @param {Object} options - Options object (for compatibility, not used)
   * @returns {Promise<Array>} Array of product objects
   */
  async listProducts(catalogNumbers, options = {}) {
    return this.getSkuDetails(catalogNumbers);
  }

  /**
   * Fetch a chunk of SKUs with deduplication
   * @param {Array} skus - Array of SKU strings
   * @returns {Promise<Array>} Array of product objects
   */
  async fetchSkuChunk(skus) {
    const catalogNumbers = skus.join(",");
    const requestKey = `chunk_${catalogNumbers}`;

    // Check if this exact request is already pending
    if (this.pendingRequests.has(requestKey)) {
      return await this.pendingRequests.get(requestKey);
    }

    try {
      // Create and store the request promise
      const requestPromise = this.makeApiRequestWithRetry(catalogNumbers);
      this.pendingRequests.set(requestKey, requestPromise);

      // Set up cleanup timeout
      const cleanupTimeoutId = setTimeout(() => {
        this.pendingRequests.delete(requestKey);
        this.requestTimeouts.delete(requestKey);
      }, this.apiConfig.timeout + 5000);

      this.requestTimeouts.set(requestKey, cleanupTimeoutId);

      // Wait for the request to complete
      const result = await requestPromise;

      // Cache the results for each individual SKU
      skus.forEach((sku) => {
        const skuResults = result.filter((product) => product.sku === sku);
        this.cache.set(sku, {
          data: skuResults,
          timestamp: Date.now(),
        });
      });

      // Clean up
      this.pendingRequests.delete(requestKey);
      const storedTimeoutId = this.requestTimeouts.get(requestKey);
      if (storedTimeoutId) {
        clearTimeout(storedTimeoutId);
        this.requestTimeouts.delete(requestKey);
      }

      return result;
    } catch (error) {
      console.error(
        `[ProductSearchAPI] Error fetching SKU details for ${catalogNumbers}:`,
        error
      );
      this.pendingRequests.delete(requestKey);
      return [];
    }
  }

  /**
   * Make API request with retry logic and exponential backoff
   * @param {string} catalogNumbers - Comma-separated SKU list
   * @param {number} retryCount - Current retry attempt
   * @returns {Promise<Array>} Transformed product data
   */
  async makeApiRequestWithRetry(catalogNumbers, retryCount = 0) {
    try {
      // Build API URL - use www.thermofisher.com for localhost, otherwise use current domain
      let baseOrigin;
      if (window.location.hostname === "localhost") {
        baseOrigin = "https://www.thermofisher.com";
      } else {
        baseOrigin = window.location.origin; // e.g., https://www.qa.thermofisher.com
      }

      const appPath = "/tools/elisabuilder"; // App base path
      const apiUrl = `${baseOrigin}${appPath}/api/magellan/getSkuDetails?catalogNumbers=${catalogNumbers}`;

      // Create fetch request with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        this.apiConfig.timeout
      );

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: this.apiConfig.headers,
        credentials: "include", // Include cookies for country/language pricing
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(
          `HTTP ${response.status}: ${response.statusText} for URL: ${apiUrl}`
        );
      }

      const data = await response.json();
      console.log(
        `[ProductSearchAPI] Successfully fetched ${
          data?.catalogs?.length || 0
        } products for: ${catalogNumbers}`
      );

      // Transform and return data
      if (data && data.catalogs && Array.isArray(data.catalogs)) {
        return this.transformProducts(data.catalogs);
      }

      return Array.isArray(data) ? this.transformProducts(data) : [];
    } catch (error) {
      console.warn(
        `[ProductSearchAPI] Request failed (attempt ${retryCount + 1}):`,
        error.message
      );

      // Retry logic with exponential backoff
      if (retryCount < this.maxRetries && !error.name?.includes("Abort")) {
        const delay = this.retryDelay * Math.pow(2, retryCount);

        await new Promise((resolve) => setTimeout(resolve, delay));
        return this.makeApiRequestWithRetry(catalogNumbers, retryCount + 1);
      }

      // Max retries exceeded or abort error
      console.error(
        `[ProductSearchAPI] Max retries exceeded for: ${catalogNumbers}`,
        error
      );
      throw error;
    }
  }

  /**
   * Transform API product data to match expected structure
   * @param {Array} apiProducts - Raw API product data
   * @returns {Array} Transformed products
   */
  transformProducts(apiProducts) {
    return apiProducts.map((data) => {
      const product = {
        name: data.productTitle || "Missing name",
        name_detail: data.skuTitle
          ? data.skuTitle.trim()
          : data.productTitle || "Missing name",
        url: data.skuRedirectUrl || data.productUrl,
        sku: data.catalogNumber,
        catalogNumber: data.catalogNumber,
        formattedSku: data.formattedCatalogNumber || data.catalogNumber,
        price: "",
        size: data.productSize,
        specifications: {},
        brand: data.umbrellaBrand || "Thermo Scientific™",
      };

      // Handle specifications
      if (data.specifications) {
        data.specifications.forEach((item) => {
          product.specifications[item.name] = item.values.join(", ");
        });
      }

      // Handle image
      if (data.images && data.images.length && data.images[0].path) {
        const imagePath = data.images[0].path;
        // Only add -250.jpg if it's not already there
        product.image = imagePath.endsWith("-250.jpg")
          ? imagePath
          : imagePath + "-250.jpg";
      } else {
        product.image =
          "data:image/png;base64,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";
      }

      return product;
    });
  }

  /**
   * Fetch pricing data for multiple SKUs using Magellan pricing API
   * @param {Array} skus - Array of SKU strings
   * @returns {Promise} Promise that resolves with pricing data (myPriceData object)
   */
  async fetchPricingData(skus) {
    try {
      // Build pricing API URL
      const baseUrlConfig = await import("../api/config");
      const baseUrl = baseUrlConfig.default;
      const skuList = skus.join(",");
      const apiUrl = `${baseUrl}/api/magellan/pricing/${skuList}?time=null&build=null`;

      console.log(
        `[ProductSearchAPI] Fetching pricing for ${skus.length} SKUs`
      );

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include", // Include cookies for country/language context
      });

      if (!response.ok) {
        throw new Error(
          `Pricing API error: ${response.status} ${response.statusText}`
        );
      }

      const rawPricingData = await response.json();

      // Return myPriceData from response
      return rawPricingData.myPriceData || {};
    } catch (error) {
      console.error("[ProductSearchAPI] Pricing API error:", error);

      // Return empty object on error - calling code will handle missing prices
      return {};
    }
  }

  /**
   * Get cookie value by name
   * @param {string} name - Cookie name
   * @returns {string|null} Cookie value or null
   */
  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(";").shift();
    }
    return null;
  }

  /**
   * Get country and language codes from cookies (Thermo Fisher standard)
   * @returns {Object} Object with countryCode and languageCode
   */
  getCountryLanguageCookies() {
    const cookies = document.cookie.split(";").reduce((acc, cookie) => {
      const [key, value] = cookie.trim().split("=");
      if (key && value) {
        acc[key] = decodeURIComponent(value);
      }
      return acc;
    }, {});

    return {
      countryCode: cookies.CK_ISO_CODE || "us",
      languageCode: cookies.CK_LANG_CODE || "en",
    };
  }



  /**
   * Validate that required cookies are present and valid
   * @returns {Object} Object with validation status and country/language codes
   */
  validateCookies() {
    const countryCode = this.getCookie("CK_ISO_CODE");
    const languageCode = this.getCookie("CK_LANG_CODE");

    const isValid = countryCode && languageCode;

    return {
      isValid,
      countryCode: countryCode || "us",
      languageCode: languageCode || "en",
      hasCountryCookie: !!countryCode,
      hasLanguageCookie: !!languageCode,
    };
  }

  /**
   * Clear expired cache entries
   */
  clearExpiredCache() {
    const now = Date.now();
    let clearedCount = 0;

    for (const [sku, cached] of this.cache.entries()) {
      if (now - cached.timestamp > this.cacheTimeout) {
        this.cache.delete(sku);
        clearedCount++;
      }
    }

    if (clearedCount > 0) {
      console.log(
        `[ProductSearchAPI] Cleared ${clearedCount} expired cache entries`
      );
    }
  }

  /**
   * Clear all cache entries
   */
  clearCache() {
    const size = this.cache.size;
    this.cache.clear();
    console.log(`[ProductSearchAPI] Cleared all ${size} cache entries`);
  }

  /**
   * Clean up resources and pending requests
   */
  cleanup() {
    // Clear all pending requests
    this.pendingRequests.clear();

    // Clear all timeouts
    for (const timeoutId of this.requestTimeouts.values()) {
      clearTimeout(timeoutId);
    }
    this.requestTimeouts.clear();

    // Clear cache
    this.clearCache();

    console.log("[ProductSearchAPI] Cleanup completed");
  }
}

// Create singleton instance
const productSearchApi = new ProductSearchApiService();

export default productSearchApi;
